import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiClient } from "@/api/client";
import { parseApiResponse, transactionSchema } from "@/api/responseSchemas";
import { useAuthStore } from "@/stores/useAuthStore";
import { useTransactionTypes } from "@/hooks/useTransactions";

const categoryTrendItemSchema = z.looseObject({
  id: z.number().optional(),
  category_id: z.number().optional(),
  category_total_expense: z.number().optional(),
  category_total_deposit: z.number().optional(),
});

const categoryTrendGroupSchema = z.looseObject({
  month: z.string().optional(),
  categories_balance: z.array(categoryTrendItemSchema).optional(),
});

const categoryTrendBalanceSchema = z.looseObject({
  monthly_categories_balance: z.array(categoryTrendGroupSchema).optional(),
  categories_balance: z.array(
    categoryTrendGroupSchema.extend({
      id: z.number().optional(),
      category_id: z.number().optional(),
      category_total_expense: z.number().optional(),
      category_total_deposit: z.number().optional(),
    }),
  ).optional(),
});

export type CategoryTrendBalanceResponse = z.infer<typeof categoryTrendBalanceSchema>;

type CategoryTrendParams = {
  categoryId: number | null;
  start: string;
  end: string;
};

export const useCategoryTrendBalance = ({
  categoryId,
  start,
  end,
}: CategoryTrendParams) => {
  const token = useAuthStore((state) => state.token);

  return useQuery<CategoryTrendBalanceResponse, Error>({
    enabled: Boolean(token && categoryId),
    queryKey: ["analyticsCategoryTrendBalance", categoryId, start, end],
    queryFn: async () => {
      const response = await apiClient.get("/categories/balance", {
        params: {
          monthly: true,
          month: "desc",
          category_id_eq: categoryId ?? undefined,
          billable_date_gte: start,
          billable_date_lte: end,
        },
      });
      return parseApiResponse(
        categoryTrendBalanceSchema,
        response.data,
        "GET /categories/balance (analytics)",
      );
    },
    placeholderData: (previousData) => previousData,
  });
};

type CategoryTransactionsParams = {
  categoryId: number | null;
  start: string;
  end: string;
};

export const useCategoryTransactions = ({
  categoryId,
  start,
  end,
}: CategoryTransactionsParams) => {
  const token = useAuthStore((state) => state.token);
  // Transaction type ids are assigned by the backend; resolving EXPENSE by name keeps this
  // working if they are ever reordered or reseeded.
  const { data: transactionTypes } = useTransactionTypes();
  const expenseTypeId = transactionTypes?.find((type) => type.name === "EXPENSE")?.id;

  return useQuery({
    enabled: Boolean(token && categoryId && expenseTypeId),
    queryKey: ["analyticsCategoryTransactions", categoryId, start, end, expenseTypeId],
    queryFn: async () => {
      const response = await apiClient.get("/transactions/", {
        params: {
          billable_date_gte: start,
          billable_date_lte: end,
          category_id: categoryId,
          type_id: expenseTypeId,
          transaction_date: "desc",
        },
      });
      return parseApiResponse(
        transactionSchema.array(),
        response.data,
        "GET /transactions (analytics)",
      );
    },
  });
};
